//
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Icon from 'app/components/atoms/Icon/Icon';
import { Popover, TextField } from '@mic3/platform-ui';
import { bind } from 'app/utils/decorators/decoratorUtils';
import Menu from 'app/components/molecules/Menu/Menu';
import MenuItem from 'app/components/molecules/Menu/MenuItem';
import { saveFilterOperators } from 'store/actions/filterbar/filterbarActions';
import Immutable from 'app/utils/immutable/Immutable';
import { get } from 'app/utils/lo/lo';
import { deepEquals, isEmpty } from 'app/utils/utils';
import { operatorsMenu } from 'app/utils/filter/operatorsUtils';
import UserTypeahead from 'app/components/organisms/Form/Typeahead/UserTypeahead';
import TeamTypeahead from 'app/components/organisms/Form/Typeahead/TeamTypeahead';
import WorkspaceTypeahead from 'app/components/organisms/Form/Typeahead/WorkspaceTypeahead';

const TYPEAHEAD_COMPONENT_MAP = {
    userTypeahead: UserTypeahead,
    teamTypeahead: TeamTypeahead,
    workspaceTypeahead: WorkspaceTypeahead,
};

const StyledIcon = styled(Icon)`
    cursor: pointer;
    margin: 10px 1rem 0 1rem;
    &:before {
        color: ${({ theme }) => theme.material.colors.secondary.main} !important;
    }
`;

const StyledGrid = styled.div`
    display: flex;
    height: 100%;
    align-items: center;

`;

class OperatorSelect extends PureComponent {
    state = {
        anchorEl: null,
        selectedItem: this.getSelectedOperator(),
    };

    componentDidUpdate(prevProps) {
        const { operatorsMap, filterViewState, filterPreferences } = this.props;
        const { operatorsMap: stateOperators } = filterViewState || {};
        if (isEmpty(stateOperators) && isEmpty(operatorsMap)) {
            // in case we reset filters
            this.setState({ selectedItem: null });
        } else if (filterPreferences?.operatorsMap && deepEquals(operatorsMap, filterPreferences?.operatorsMap)) {
            // in case we load preferences
            const selectedItem = this.getSelectedOperator();
            this.setState({ selectedItem });
        }
    }

    @bind
    getFieldType() {
        const { operatorType, type } = this.props;
        return operatorType || type;
    }

    @bind
    getSelectedOperator() {
        const { name, filterPreferences, filterViewState, opValue, operatorsMap } = this.props;
        const { operatorsMap: prefOperators } = filterPreferences || {};
        const { operatorsMap: stateOperators } = filterViewState || {};
        const operatorValue = operatorsMap?.[name] || stateOperators?.[name] || prefOperators?.[name] || opValue;
        if (!operatorValue) {
            return null;
        }
        return operatorsMenu?.[this.getFieldType()]?.find(op => op?.value === operatorValue);
    }

    @bind
    openMenu(event) {
        this.setState({ anchorEl: event.currentTarget });
    }

    @bind
    closeMenu(event) {
        this.setState({ anchorEl: null });
    }

    onMenuItemClick = selectedItem => async () => {
        const { name, saveFilterOperators, operatorsMap, filterPreferences, filterViewState } = this.props;
        const prevOperator = operatorsMap[name] || filterViewState?.operatorsMap?.[name] || filterPreferences?.operatorsMap?.[name];
        await saveFilterOperators({ [name]: selectedItem?.value });
        await this.setState({ selectedItem, anchorEl: null });
        if (selectedItem?.value === 'is null') {
            return this.setValue('__isNull');
        }
        if (prevOperator === 'is null' && selectedItem?.value !== 'is null') {
            return this.setValue('');
        }
    };

    @bind
    setValue(value) {
        const { name } = this.props;
        this.props.onChange({ target: { type: this.getFieldType(), value, name } });
    }

    @bind
    renderPopoverMenu() {
        const { anchorEl } = this.state;
        const fieldType = this.getFieldType();
        const open = Boolean(anchorEl);
        return (
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={this.closeMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Menu>
                    {Object.entries(operatorsMenu[fieldType])?.map(([key, data]) => (
                        <MenuItem
                            key={key}
                            icon={data?.icon}
                            iconType={data?.iconType}
                            style={{ width: '180px' }}
                            onClick={this.onMenuItemClick(data)}
                            name={data?.label}
                        />
                    ))}
                </Menu>
            </Popover>
        );
    }

    render() {
        const { selectedItem } = this.state;
        const {
            type,
            operatorType,
            opSelector,
            filterViewState,
            filterBarId,
            filterIsOpen,
            operatorsMap,
            saveFilterOperators,
            defaultUnit,
            filterPreferences,
            customField,
            onChange,
            ...props
        } = this.props;
        const fieldType = this.getFieldType();
        if (!Object.keys(operatorsMenu).includes(fieldType)) {
            return <TextField {...props} type={type} />;
        }

        const disabled = props.disabled || props.value === '__isNull';
        const val = props.value === '__isNull' ? '' : props.value;
        const FieldComponent = TYPEAHEAD_COMPONENT_MAP[fieldType] || TextField;
        const isUserTypeahead = fieldType === 'userTypeahead';

        return (
            <>
                <StyledGrid id='opertorfield'>
                    <StyledIcon noBackground name={selectedItem?.icon || operatorsMenu?.[fieldType]?.[0]?.icon} onClick={this.openMenu} />
                    <FieldComponent
                        {...props}
                        disabled={disabled}
                        value={val}
                        onChange={e => onChange(e)}
                        {...(isUserTypeahead && { includeDisabledUsers: true })}
                    />
                </StyledGrid>
                {this.renderPopoverMenu()}
            </>
        );
    }
}

export default connect(
    (state, props) =>
        Immutable({
            filterViewState: get(state, `component.state.${state.filterbar.id}`),
            filterPreferences: get(state, `user.preferences.filters.${state.filterbar.id}`),
            filterBarId: state.filterbar.id,
            filterIsOpen: state.filterbar.isOpen,
            operatorsMap: state.filterbar.operatorsMap,
        }),
    { saveFilterOperators }
)(OperatorSelect);
